import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { z } from 'zod';

/**
 * Minimum allowed policy check interval for production environment (3 hours)
 * Prevents too frequent checks in production
 */
const MIN_POLICY_CHECK_HOURS_PROD = 3;

/**
 * Minimum allowed policy check interval for staging/dev environment (0.01 hours ≈ 36 seconds)
 * Allows rapid testing in non-production environments
 */
const MIN_POLICY_CHECK_HOURS_DEV = 0.01;

/**
 * Get minimum allowed policy check interval based on environment
 * - Production: 3 hours (prevents too frequent checks)
 * - Staging/Dev: 0.01 hours (~36 seconds, for easier testing)
 */
function getMinPolicyCheckHours(): number {
  const env = getEnvironment();
  return env === ENV_NAME.PROD ? MIN_POLICY_CHECK_HOURS_PROD : MIN_POLICY_CHECK_HOURS_DEV;
}

/**
 * Force Update Policy Schema
 * Matches the JSON payload of the Firebase Remote Config parameter
 * 'desktop_force_update_policy'
 */
export const ForceUpdatePolicySchema = z.object({
  min_supported_version: z.string(),
  blocked_versions: z.array(z.string()).optional().default([]),
  // Lazy so the environment lookup happens at parse time, not module load —
  // this schema is imported (via the flags barrel) into many test module
  // graphs that mock `@heidi/api/config` after imports evaluate.
  policy_check_in_hours: z
    .lazy(() => z.number().min(getMinPolicyCheckHours()).max(24))
    .optional()
    .default(6),
  recommended_message: z.string().optional().default(''),
  policy_updated_at: z.string().datetime(), // ISO 8601 datetime string, validated format (e.g., "2025-01-15T00:00:00Z")
  force_update_deadline: z.string().datetime(), // ISO 8601 datetime string, validated format (e.g., "2025-01-15T00:00:00Z")
});

export type ForceUpdatePolicy = z.infer<typeof ForceUpdatePolicySchema>;

/**
 * Policy evaluation result states
 * - OK: Current version is compliant, no action needed
 * - GRACE: Current version needs update but within grace period
 * - FORCED: Current version must update immediately (blocked or grace expired)
 */
export const POLICY_EVALUATION = Object.freeze({
  OK: 'OK',
  GRACE: 'GRACE',
  FORCED: 'FORCED',
} as const);

export type PolicyEvaluationResult =
  (typeof POLICY_EVALUATION)[keyof typeof POLICY_EVALUATION];

/**
 * Evaluation context passed to policy evaluator
 */
export interface PolicyEvaluationContext {
  currentVersion: string;
  policy: ForceUpdatePolicy;
  isRecording: boolean;
}

/**
 * Detailed evaluation result with metadata
 */
export interface PolicyEvaluation {
  result: PolicyEvaluationResult;
  reason?: 'blocked_version' | 'min_version_expired' | 'grace_period_active';
  deadlineAt?: Date;
}
